import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileSignature, Building } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Home = () => {
  const { user } = useAuth();

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <div className="flex justify-center mb-6">
          <div className="p-3 bg-blue-100 rounded-lg">
            <FileSignature className="h-12 w-12 text-blue-600" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Welcome to SureSign
        </h1>
        <p className="text-xl text-gray-600">
          Streamlined lease signing and management for student housing
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {/* For Students */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4">For Students</h2>
          <ul className="space-y-4">
            {[
              'Easy onboarding process',
              'Digital lease signing',
              'Secure document storage',
              'Real-time application status'
            ].map((feature) => (
              <li key={feature} className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          {!user ? (
            <Link
              to="/login"
              className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          ) : user.role === 'student' && (
            <Link
              to="/onboarding"
              className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Continue Onboarding <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          )}
        </div>

        {/* For Property Owners */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4">For Property Owners</h2>
          <ul className="space-y-4">
            {[
              'List your properties',
              'Manage applications',
              'Digital lease management',
              'Secure payment system'
            ].map((feature) => (
              <li key={feature} className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/register-property"
            className="mt-6 inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            Register Property <Building className="ml-2 h-4 w-4" />
          </Link>
        </div>

        {/* For Administrators */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4">For Administrators</h2>
          <ul className="space-y-4">
            {[
              'Manage lease agreements',
              'Track signatures',
              'Generate invoices',
              'Monitor compliance'
            ].map((feature) => (
              <li key={feature} className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          {!user ? (
            <Link
              to="/login"
              className="mt-6 inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Admin Login <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          ) : user.role !== 'student' && (
            <Link
              to="/admin/dashboard"
              className="mt-6 inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Property Showcase */}
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <img
            src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267"
            alt="21 Saratoga"
            className="w-full h-48 object-cover rounded-lg mb-4"
          />
          <h3 className="text-xl font-semibold mb-2">21 Saratoga</h3>
          <p className="text-gray-600 mb-4">
            Premium student accommodation in a prime location, offering modern amenities and a vibrant community atmosphere.
          </p>
          <div className="flex flex-wrap gap-2">
            {['24/7 Security', 'High-Speed WiFi', 'Study Areas', 'Laundry'].map((amenity) => (
              <span key={amenity} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                {amenity}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <img
            src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00"
            alt="Highlands Lofts"
            className="w-full h-48 object-cover rounded-lg mb-4"
          />
          <h3 className="text-xl font-semibold mb-2">Highlands Lofts</h3>
          <p className="text-gray-600 mb-4">
            Modern urban living in the heart of the city, designed for students who appreciate style and convenience.
          </p>
          <div className="flex flex-wrap gap-2">
            {['Gym', 'Common Room', 'Bike Storage', 'Security'].map((amenity) => (
              <span key={amenity} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                {amenity}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;